import { Container, Reveal, RevealGroup, Counter } from '@/components/common'
import type { Member } from '@/types/team'
import { featuredMember, teamMembers } from '@/constants/team'
import { board } from '@/constants/advisory-board'
import { consultants } from '@/constants/consultant'

const teamStats = [
  { value: teamMembers.length + 1, suffix: '', label: 'Executive Team Members' },
  { value: board.length, suffix: '', label: 'Advisory Board Members' },
  { value: consultants.length, suffix: '', label: 'Strategic Consultants' },
]

const MemberCard = ({ name, role, image }: Member) => (
  <article className="group cursor-default">
    <div className="relative overflow-hidden">
      <img
        src={image}
        alt={`Portrait of ${name}, ${role} at IBON World`}
        className="aspect-4/5 w-full object-cover object-top grayscale-[55%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-ibon-dark/0 transition-colors duration-500 group-hover:bg-ibon-dark/10" />
    </div>
    <div className="mt-5 flex items-start justify-between gap-2">
      <div>
        <h3 className="text-base font-bold leading-snug tracking-tight text-ibon-dark">{name}</h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-text-muted transition-colors duration-300 group-hover:text-ibon-sky">
          {role}
        </p>
      </div>
      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ibon-sky opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </div>
  </article>
)

export const Team = () => {
  return (
    <section id="team" className="bg-white py-24 lg:py-32">
      <Container>
        {/* Header */}
        <div className="max-w-3xl">
          <Reveal variant="up" delay={100}>
            <h2 className="mt-7 text-4xl font-bold leading-[1.02] tracking-[-0.02em] text-ibon-dark sm:text-5xl lg:text-6xl">
              Dedicated hearts behind <span className="text-ibon-dark/30">our mission.</span>
            </h2>
          </Reveal>
          <Reveal variant="up" delay={200}>
            <p className="mt-8 text-lg leading-relaxed text-text-muted">
              A passionate team of leaders, creators, and changemakers driving entrepreneurship,
              education, and cultural exchange across Africa and the Caribbean.
            </p>
          </Reveal>
        </div>

        {/* Stats */}
        <RevealGroup className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3" step={90}>
          {teamStats.map((stat) => (
            <div key={stat.label} className="border-l-2 border-ibon-sky pl-5">
              <div className="text-4xl font-bold tracking-tight text-ibon-dark sm:text-5xl">
                <Counter value={stat.value} suffix={stat.suffix} />
              </div>
              <p className="mt-2 text-sm font-medium uppercase tracking-wide text-text-muted">
                {stat.label}
              </p>
            </div>
          ))}
        </RevealGroup>

        {/* Featured: President */}
        <Reveal variant="up" delay={100} className="mt-20">
          <article className="group grid cursor-default gap-0 lg:grid-cols-[1fr_1fr]">
            <div className="relative overflow-hidden">
              <img
                src={featuredMember.image}
                alt={`Portrait of ${featuredMember.name}, ${featuredMember.role} at IBON World`}
                className="aspect-4/5 w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 lg:aspect-auto lg:h-full lg:min-h-[560px]"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col justify-end bg-surface p-10 lg:p-14">
              <span className="text-[8rem] font-bold leading-none text-ibon-dark/6 select-none lg:text-[12rem]">
                01
              </span>
              <p className="mt-auto text-xs font-semibold uppercase tracking-[0.25em] text-ibon-sky">
                {featuredMember.role}
              </p>
              <h3 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-ibon-dark lg:text-5xl">
                {featuredMember.name}
              </h3>
              <p className="mt-6 max-w-sm text-base leading-relaxed text-text-muted">
                Leading IBON World's mission to connect entrepreneurs, educators, and communities
                across Africa and the Caribbean through transformative programmes and partnerships.
              </p>
              <div className="mt-8 h-px w-12 bg-ibon-sky transition-all duration-500 group-hover:w-32" />
            </div>
          </article>
        </Reveal>

        {/* Rest of team */}
        <RevealGroup
          className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 sm:grid-cols-3 lg:grid-cols-4"
          step={70}
        >
          {teamMembers.map((member) => (
            <MemberCard key={member.name} {...member} />
          ))}
        </RevealGroup>
      </Container>
    </section>
  )
}
